import {
  Component,
  Injector,
  OnDestroy,
  OnInit,
  ViewEncapsulation,
} from '@angular/core';
import { Subscription } from 'rxjs';
import { IUpdateEvent } from '../../../models/layout-config/layout-configuration.model';
import { SelectionResults } from '../../../models/selection-results/selection-results.model';
import { ValidSize } from '../../../models/selection-results/valid-size.model';
import { BaseGridComponent } from '../../base-grid/base-grid.component';
import { ColumnList } from '../../constants/column-list.const';

@Component({
  selector: 'ceiling-cabinet-grid',
  templateUrl: '../../base-grid/base-grid.component.html',
  styleUrls: ['../../base-grid/base-grid.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class CeilingCabinetGridComponent
  extends BaseGridComponent
  implements OnInit, OnDestroy
{
  private selectionResultsSubscription: Subscription;

  constructor(protected injector: Injector) {
    super(injector);

    this.gridVersion = '1';

    this.footerProductType = 'Fan(s)';
  }

  ngOnInit() {
    this.columnList = ColumnList.getColumns(
      'actualCFM',
      'relativeCost',
      'budgetPrice',
      'operatingCost',
      'watts',
      'sphericalSones',
      'bhp',
      'fei',
      'feiFactorsInputPower',
      'inletSones',
      'fanRPM',
      'weight',
      'amca',
      'operatingStaticPressure',
      'wheelDiameter',
      'motorTechnology',
      'outletVelocity',
      'staticEfficiency',
      'totalEfficiency',
      'peakTotalEfficiency',
      'pointsWithinPeakTotalEfficiency',
      'speedNonAdjusted',
      'hasSpeedController',
      'hasBaffle',
      'speedMax',
      'tipSpeed',
      'inletOctaveBand63Hz',
      'inletOctaveBand125Hz',
      'inletOctaveBand250Hz',
      'inletOctaveBand500Hz',
      'inletOctaveBand1kHz',
      'inletOctaveBand2kHz',
      'inletOctaveBand4kHz',
      'inletOctaveBand8kHz',
      'inletLwa',
    );

    this.setVisibleColumns([
      'actualCFM',
      'relativeCost',
      'operatingCost',
      'motorTechnology',
      'watts',
      'sphericalSones',
      'fanRPM',
      'weight',
      'amca',
    ]);

    this.selectionResultsSubscription = this.selectionResultsReceived.subscribe(
      (selectionResults: SelectionResults) => {
        const newData = [];

        selectionResults.validSizes.forEach((size) => {
          newData.push(this.validSizeToDSRow(size));
        });

        this.dataSource.data = newData;
      },
    );

    this.updateColumnHelp();
  }

  ngOnDestroy() {
    super.ngOnDestroy();

    this.selectionResultsSubscription.unsubscribe();
  }

  private validSizeToDSRow(size: ValidSize) {
    const rowData = {
      rank: size.sorting.recommendedOrder,
      name: size.name,
      actualCFM: size.outputs.operatingVolume,
      operatingCost: size.outputs.operatingCost,
      watts: size.outputs.watts,
      sphericalSones: size.outputs.inletSound.sphericalSones,
      relativeCost: size.outputs.relativeCost,
      budgetPrice: size.pricing.price,
      bhp: size.outputs.operatingPower,
      inletSones: size.outputs.inletSound.sones,
      fanRPM: size.outputs.speed,
      weight: size.pricing.weight,
      driveLossPercentage: size.outputs.driveLossPercentage,
      amca: size.amcaRatings,
      operatingStaticPressure: size.outputs.operatingStaticPressure,
      wheelDiameter: size.outputs.wheelDiameter,
      motorTechnology: this.getFanMotorTechnology(size),
      outletVelocity: size.outputs.outletVelocity,
      staticEfficiency: size.outputs.staticEfficiency,
      feg: size.outputs.feg,
      fei: size.outputs.fei,
      feiFactorsInputPower: size.outputs.feiFactorsInputPower,
      totalEfficiency: size.outputs.totalEfficiency,
      peakTotalEfficiency: size.outputs.peakTotalEfficiency,
      pointsWithinPeakTotalEfficiency:
        size.outputs.pointsWithinPeakTotalEfficiency,
      speedNonAdjusted: size.outputs.speedNonAdjusted,
      hasSpeedController: size.outputs.hasSpeedController,
      hasBaffle: size.outputs.hasBaffle,
      speedMax: size.outputs.speedMax,
      tipSpeed: size.outputs.tipSpeed,
      inletOctaveBand63Hz: size.outputs.inletSound.octaveBand63Hz,
      inletOctaveBand125Hz: size.outputs.inletSound.octaveBand125Hz,
      inletOctaveBand250Hz: size.outputs.inletSound.octaveBand250Hz,
      inletOctaveBand500Hz: size.outputs.inletSound.octaveBand500Hz,
      inletOctaveBand1kHz: size.outputs.inletSound.octaveBand1kHz,
      inletOctaveBand2kHz: size.outputs.inletSound.octaveBand2kHz,
      inletOctaveBand4kHz: size.outputs.inletSound.octaveBand4kHz,
      inletOctaveBand8kHz: size.outputs.inletSound.octaveBand8kHz,
      inletLwa: size.outputs.inletSound.lwa,
      stable: size.stability.stable,
      sizeData: size,
    };

    return rowData;
  }

  protected override layoutConfigUpdate = (updates?: IUpdateEvent[]) => {
    if (!!this._layoutConfig) {
      const question = this._layoutConfig.getQuestion('AvailableSizes');

      if (question?.value === 'Recommended') {
        const answer = question.answers.find((a) => a.name === question.value);
        this.footerProductType = `${answer?.text ?? 'Optimized'} Fan(s)`;
      } else if (this.footerProductType !== 'Fan(s)') {
        this.footerProductType = 'Fan(s)';
      }
    }
  };
}
